import * as React from "react";
import { inject, observer } from "mobx-react";
import * as fileUtil from "../../common/Methods/FileUtil";

import { ComplianceAttachmentLog, AttachmentFile, AttachmentLog } from "src/model/Attachment";
import { SelectGroup } from "../common/SimpleFormComponents";
import Dropzone, { useDropzone } from "react-dropzone";
import { UncontrolledTooltip } from "reactstrap";
import * as moment from "moment";
import { successMessage, errorMessage } from "../common/Toast";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faFileUpload,
    faDownload,
    faTrashAlt,
    faExclamationTriangle,
    faTimes,
    faCheck,
    faFileInvoiceDollar
} from "@fortawesome/free-solid-svg-icons";

import { HorseForSaleStore } from "src/stores/HorseForSaleStore";
import { AttachmentLogEndpoints } from "src/endpoints/AttachmentLogEndpoints";

import { SimpleConfirmation } from "../common/SimpleConfirmAlert";
import { FormDialog } from "../common/FormDialog";
import { TextareaGroup } from "../common/SimpleFormComponents";
import { ButtonWithTooltip } from "../common/ButtonWithTooltip";

const NONE_ID = -1;
const DEFAULT_DOC_ID = 0;

const ComplianceActions = ({
    logAndLabel,
    markLogNotCompliant,
    handleOverrideFile
}: {
    logAndLabel: AttachmentLogAndLabel;
    markLogNotCompliant: (logAndLabel: AttachmentLogAndLabel) => void;
    handleOverrideFile: (currentOverrideType: AttachmentLogAndLabel) => (e: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
    if ((logAndLabel.log as ComplianceAttachmentLog).madeCompliant) {
        const complianceNotes = (logAndLabel.log as ComplianceAttachmentLog).complianceNotes;
        return (
            <div>
                <span id={`tooltipNotes${logAndLabel.log.id}`} className="text-success">
                    <FontAwesomeIcon icon={faCheck} className="mr-2" />
                    Compliant
                    {complianceNotes && (
                        <span className="badge badge-info  ml-2" style={{ position: "relative", top: -2 }}>
                            {" "}
                            Notes
                        </span>
                    )}
                </span>
                {complianceNotes && (
                    <UncontrolledTooltip placement="top" target={`tooltipNotes${logAndLabel.log.id}`} delay={0} className="tooltipstrap">
                        {(logAndLabel.log as ComplianceAttachmentLog).complianceNotes}
                    </UncontrolledTooltip>
                )}
                <div>
                    <button
                        className="btn btn-outline-secondary btn-sm mt-3 py-2"
                        type="button"
                        onClick={() => markLogNotCompliant(logAndLabel)}
                        style={{ lineHeight: "1rem" }}
                    >
                        Make not-compliant
                    </button>
                </div>
            </div>
        );
    } else {
        return (
            <>
                <div className="text-danger">
                    <FontAwesomeIcon icon={faExclamationTriangle} className=" mr-2" />
                    Not-compliant
                </div>
                <button type="button" className="btn btn-primary btn-sm mt-3" title="Override" onClick={handleOverrideFile(logAndLabel)}>
                    Override
                </button>
            </>
        );
    }
};

export interface State {
    selectedFile: File | null;
    selectedDocId: number;
    checkedAsCompliant: boolean;
    wasValidated: boolean;
    showNewOverrideDialog: boolean;
    currentOverrideType: AttachmentLogAndLabel | null;
    currentOverrideNote: string;
}

interface DocumentList extends ComplianceAttachmentLog {
    name: string;
}

export interface AttachmentLogAndLabel {
    log: AttachmentLog;
    name: string;
}

export interface Props {
    horseForSaleStore?: HorseForSaleStore;
    attachmentLogEndpoints?: AttachmentLogEndpoints;
    title: string;
    documentsList: Array<AttachmentLogAndLabel>;
    showStatus: boolean;
    className?: string;
    isCompliant?: boolean;
}

@inject("attachmentLogEndpoints")
@inject("horseForSaleStore")
@observer
export default class DocumentsAndComplianceComponent extends React.Component<Props, State> {
    private form: HTMLFormElement | null;
    private formNotes: HTMLFormElement | null;

    constructor(props: Props) {
        super(props);
        this.state = {
            selectedFile: null,
            selectedDocId: DEFAULT_DOC_ID,
            checkedAsCompliant: true,
            wasValidated: false,
            showNewOverrideDialog: false,
            currentOverrideType: null,
            currentOverrideNote: ""
        };
    }

    addAttachment = async (file: File) => {
        const fileBase64 = await fileUtil.FileToBase64(file);
        this.props
            .horseForSaleStore!.addAttachment(this.state.selectedDocId, file.name, file.type, fileBase64, this.state.checkedAsCompliant)
            .then(() => {
                successMessage(`${file.name} has been attached.`);
                this.setState({ selectedFile: null, selectedDocId: DEFAULT_DOC_ID, checkedAsCompliant: true });
            })
            .catch(error => errorMessage(error));
    };

    deleteAttachment = (file: AttachmentFile) => async (event: React.MouseEvent<HTMLButtonElement>) => {
        const goAhead = await SimpleConfirmation(`Are you sure you'd like to delete ${file.name} file?`);
        if (!goAhead) {
            return;
        }

        this.props
            .horseForSaleStore!.deleteAttachment(file.id)
            .then(() => successMessage(`${file.name} has been deleted.`))
            .catch(error => errorMessage(error));
    };

    getAttachment = (file: AttachmentFile) => async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        this.props
            .attachmentLogEndpoints!.getAttachment(file.id)
            .then((blob: Blob) => fileUtil.DownloadBlob(file.name, blob))
            .catch(err => console.error(err));
    };

    submit = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (this.form && this.form.checkValidity() && this.state.selectedDocId !== DEFAULT_DOC_ID && this.state.selectedFile !== null) {
            this.setState({ wasValidated: false });
            this.addAttachment(this.state.selectedFile as File);
        } else {
            this.setState({ wasValidated: true });
        }
    };

    markLogCompliant = async () => {
        try {
            await this.props.horseForSaleStore!.markLogCompliant(this.state.currentOverrideType!.log.id, this.state.currentOverrideNote);
            successMessage(`${this.state.currentOverrideType!.name} has been made compliant.`);
        } catch (err) {
            errorMessage(err);
        }
    };

    markLogNotCompliant = async (logAndLabel: AttachmentLogAndLabel) => {
        const goAhead = await SimpleConfirmation(`Are you sure you'd like to mark ${logAndLabel.name} as not-compliant?`);
        if (!goAhead) {
            return;
        }

        try {
            await this.props.horseForSaleStore!.markLogNotCompliant(logAndLabel.log.id);
            successMessage(`${logAndLabel.name} has been marked as not-compliant.`);
        } catch (err) {
            errorMessage(err);
        }
    };

    submitOverride = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        this.markLogCompliant();
        this.clearOverrideForm();
    };

    clearOverrideForm = () => {
        this.setState({ showNewOverrideDialog: false, currentOverrideNote: "" });
    };

    handleOverrideFile = (currentOverrideType: AttachmentLogAndLabel) => (e: React.MouseEvent<HTMLButtonElement>): void => {
        e.preventDefault();
        this.setState({ showNewOverrideDialog: true, currentOverrideType: currentOverrideType });
    };

    generateRegistrationCertificateFeeInvoice = async (type: "GENERATE" | "UPDATE") => {
        const horseForSale = this.props.horseForSaleStore!.horseForSale!;

        const goAhead = await SimpleConfirmation(
            `Are you sure you'd like to ${type.toLowerCase()} the invoice for the missing Registration Certificate for ${horseForSale.horse.name}? ${
                type === "UPDATE" ? "Existing invoices will be voided." : ""
            }`
        );
        if (!goAhead) {
            return;
        }

        try {
            await this.props.horseForSaleStore!.generateRegistrationCertificateFeeInvoice(horseForSale.id);
            successMessage(`Invoice successfully ${type.toLowerCase()}d.`);
        } catch (err) {
            errorMessage(err);
        }
    };

    generateCogginsCertificateFeeInvoice = async (type: "GENERATE" | "UPDATE") => {
        const horseForSale = this.props.horseForSaleStore!.horseForSale!;

        const goAhead = await SimpleConfirmation(
            `Are you sure you'd like to ${type.toLowerCase()} the invoice for the missing Coggins Certificate for ${horseForSale.horse.name}? ${
                type === "UPDATE" ? "Existing invoices will be voided." : ""
            }`
        );
        if (!goAhead) {
            return;
        }

        try {
            await this.props.horseForSaleStore!.generateCogginsCertificateFeeInvoice(horseForSale.id);
            successMessage(`Invoice successfully ${type.toLowerCase()}d.`);
        } catch (err) {
            errorMessage(err);
        }
    };

    render() {
        const hfs = this.props.horseForSaleStore!.horseForSale!;

        return (
            <>
                <div className={`card ${this.props.className}`}>
                    <div className="card-header">
                        {this.props.showStatus &&
                            (this.props.isCompliant ? (
                                <FontAwesomeIcon icon={faCheck} className="text-success mr-3" />
                            ) : (
                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger mr-3" />
                            ))}
                        {this.props.title}
                    </div>
                    <div className="card-body">
                        <form
                            ref={form => (this.form = form)}
                            id="attach-doc-form"
                            className={"needs-validation" + (this.state.wasValidated ? " was-validated" : "")}
                            onSubmit={e => this.submit(e)}
                            noValidate={true}
                        >
                            <div className="form-row">
                                <SelectGroup
                                    label="Type:"
                                    name="documentType"
                                    isInvalid={this.state.wasValidated && this.state.selectedDocId === DEFAULT_DOC_ID}
                                    isRequired={true}
                                    className={`col-md-12 ${!this.props.showStatus ? `col-lg-6` : `col-lg-4`}`}
                                    disabled={false}
                                    value={this.state.selectedDocId}
                                    onChange={e => {
                                        e.preventDefault();
                                        const index = e.target.selectedIndex;
                                        this.setState({ selectedDocId: parseInt(e.target.value, 10) });
                                    }}
                                >
                                    <option key={DEFAULT_DOC_ID} value={DEFAULT_DOC_ID}>
                                        {" "}
                                        Select a document...{" "}
                                    </option>
                                    {this.props.documentsList.map((label: AttachmentLogAndLabel) => (
                                        <option key={label.log.id} value={label.log.id}>
                                            {" "}
                                            {label.name}{" "}
                                        </option>
                                    ))}
                                </SelectGroup>
                                <div className={`form-group col-md-12 ${!this.props.showStatus ? `col-lg-6` : `col-lg-5`}`}>
                                    <label htmlFor="files">
                                        Attach File: <span className="text-danger">*</span>
                                    </label>
                                    {this.state.selectedFile === null && (
                                        <Dropzone
                                            onDrop={acceptedFiles => acceptedFiles.map(file => this.setState({ selectedFile: file }))}
                                            multiple={false}
                                        >
                                            {({ getRootProps, getInputProps }) => (
                                                <section>
                                                    <div
                                                        {...getRootProps()}
                                                        className={`rounded attachmentLog 
                                                        ${this.state.selectedFile === null && this.state.wasValidated ? `empty-attachament` : ``}`}
                                                        style={{ padding: "0.37rem .25rem" }}
                                                    >
                                                        <input {...getInputProps()} />

                                                        <p className="text-center text-secondary my-0" style={{ opacity: 0.6 }}>
                                                            Drag &amp; drop here or click to upload a file
                                                        </p>
                                                    </div>
                                                </section>
                                            )}
                                        </Dropzone>
                                    )}
                                    {this.state.selectedFile !== null && (
                                        <button
                                            className="btn btn-secondary btn-sm btn-block d-flex py-2"
                                            onClick={() => this.setState({ selectedFile: null })}
                                        >
                                            {this.state.selectedFile.name}
                                            <FontAwesomeIcon icon={faTimes} className="mt-1 ml-auto" style={{ opacity: 0.6 }} />
                                        </button>
                                    )}
                                </div>
                                {this.props.showStatus && (
                                    <div className="form-group col-md-12 col-lg-3 pt-4">
                                        <div className="form-check mt-3 ml-3">
                                            <input
                                                className="form-check-input not-required"
                                                type="checkbox"
                                                checked={this.state.checkedAsCompliant}
                                                required={false}
                                                id="defaultCheck1"
                                                onChange={() => this.setState({ checkedAsCompliant: !this.state.checkedAsCompliant })}
                                            />
                                            <label className="form-check-label ml-2 not-required" htmlFor="defaultCheck1">
                                                Mark as compliant
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="form-row mb-4">
                                <div className="form-group col-md-12 col-lg-12 text-right">
                                    <button type="submit" className="btn btn-info btn-sm btn-block">
                                        <FontAwesomeIcon icon={faFileUpload} className="mr-2" />
                                        Upload Document
                                    </button>
                                </div>
                            </div>
                        </form>
                        <div className="table-responsive">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th scope="col">Type</th>
                                        <th scope="col">File Name</th>
                                        <th scope="col">Date Uploaded</th>
                                        <th scope="col" style={{ textAlign: "center" }}>
                                            Actions
                                        </th>
                                        {this.props.showStatus && (
                                            <th scope="col" style={{ textAlign: "center" }}>
                                                Status
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.props.documentsList.map((logAndLabel: AttachmentLogAndLabel) =>
                                        logAndLabel.log.files.length === 0 ? (
                                            <tr key={logAndLabel.log.id}>
                                                <td>{logAndLabel.name}</td>
                                                <td>-</td>
                                                <td>-</td>
                                                <td style={{ textAlign: "center" }}>
                                                    {logAndLabel.name === "Registration Certificate" && (
                                                        <ButtonWithTooltip
                                                            onClick={() =>
                                                                this.generateRegistrationCertificateFeeInvoice(
                                                                    hfs.owners.find(o => o.registrationInvoice !== null) ? "UPDATE" : "GENERATE"
                                                                )
                                                            }
                                                            id={`invoiceRegistrationCertificate${logAndLabel.log.id}`}
                                                            icon={<FontAwesomeIcon icon={faFileInvoiceDollar} style={{ margin: "0 2px" }} />}
                                                            title={
                                                                (hfs.owners.find(o => o.registrationInvoice !== null) ? "Update" : "Generate") +
                                                                " invoice for missing Registration Certificate"
                                                            }
                                                            buttonStyle="btn btn-info btn-sm mr-2 mb-1"
                                                            disabled={hfs.owners.length === 0}
                                                        />
                                                    )}
                                                    {logAndLabel.name === "Coggins Certificate" && (
                                                        <ButtonWithTooltip
                                                            onClick={() =>
                                                                this.generateCogginsCertificateFeeInvoice(
                                                                    hfs.owners.find(o => o.cogginsInvoice !== null) ? "UPDATE" : "GENERATE"
                                                                )
                                                            }
                                                            id={`invoiceCogginsCertificate${logAndLabel.log.id}`}
                                                            icon={<FontAwesomeIcon icon={faFileInvoiceDollar} style={{ margin: "0 2px" }} />}
                                                            title={
                                                                (hfs.owners.find(o => o.cogginsInvoice !== null) ? "Update" : "Generate") +
                                                                " invoice for missing Coggins Certificate"
                                                            }
                                                            buttonStyle="btn btn-info btn-sm mr-2 mb-1"
                                                            disabled={hfs.owners.length === 0}
                                                        />
                                                    )}
                                                </td>
                                                {this.props.showStatus && (
                                                    <td scope="col" style={{ textAlign: "center" }}>
                                                        <ComplianceActions
                                                            logAndLabel={logAndLabel}
                                                            markLogNotCompliant={this.markLogNotCompliant}
                                                            handleOverrideFile={this.handleOverrideFile}
                                                        />
                                                    </td>
                                                )}
                                            </tr>
                                        ) : (
                                            logAndLabel.log.files
                                                .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0))
                                                .map((file, i) => (
                                                    <tr key={logAndLabel.log.id + i}>
                                                        {i === 0 && <td rowSpan={logAndLabel.log.files.length}>{logAndLabel.name}</td>}
                                                        <td>{file.name}</td>
                                                        <td>{moment(file.dateUploaded).format("MM/DD/YYYY")}</td>
                                                        <td>
                                                            <button
                                                                type="text"
                                                                className="btn btn-secondary btn-sm mr-2 mb-1"
                                                                id={`tooltipDownload${file.id}`}
                                                                title="Download File"
                                                                onClick={this.getAttachment(file)}
                                                            >
                                                                <FontAwesomeIcon icon={faDownload} />
                                                            </button>
                                                            <UncontrolledTooltip
                                                                placement="top"
                                                                target={`tooltipDownload${file.id}`}
                                                                delay={0}
                                                                className="tooltipstrap"
                                                            >
                                                                Download File
                                                            </UncontrolledTooltip>
                                                            <button
                                                                type="text"
                                                                className="btn btn-secondary btn-sm mr-2 mb-1"
                                                                id={`tooltipDelete${file.id}`}
                                                                title="Delete File"
                                                                onClick={this.deleteAttachment(file)}
                                                            >
                                                                <FontAwesomeIcon icon={faTrashAlt} />
                                                            </button>
                                                            <UncontrolledTooltip
                                                                placement="top"
                                                                target={`tooltipDelete${file.id}`}
                                                                delay={0}
                                                                className="tooltipstrap"
                                                            >
                                                                Delete File
                                                            </UncontrolledTooltip>
                                                            {logAndLabel.name === "Registration Certificate" &&
                                                                (logAndLabel.log as ComplianceAttachmentLog).madeCompliant === null && (
                                                                    <ButtonWithTooltip
                                                                        onClick={() =>
                                                                            this.generateRegistrationCertificateFeeInvoice(
                                                                                hfs.owners.find(o => o.registrationInvoice !== null)
                                                                                    ? "UPDATE"
                                                                                    : "GENERATE"
                                                                            )
                                                                        }
                                                                        id={`invoiceRegistrationCertificate${logAndLabel.log.id}`}
                                                                        icon={
                                                                            <FontAwesomeIcon icon={faFileInvoiceDollar} style={{ margin: "0 2px" }} />
                                                                        }
                                                                        title={
                                                                            (hfs.owners.find(o => o.registrationInvoice !== null)
                                                                                ? "Update"
                                                                                : "Generate") + " Invoice for missing Registration Certificate"
                                                                        }
                                                                        buttonStyle="btn btn-info btn-sm mr-2 mb-1"
                                                                        disabled={hfs.owners.length === 0}
                                                                    />
                                                                )}
                                                            {logAndLabel.name === "Coggins Certificate" &&
                                                                (logAndLabel.log as ComplianceAttachmentLog).madeCompliant === null && (
                                                                    <ButtonWithTooltip
                                                                        onClick={() =>
                                                                            this.generateCogginsCertificateFeeInvoice(
                                                                                hfs.owners.find(o => o.cogginsInvoice !== null)
                                                                                    ? "UPDATE"
                                                                                    : "GENERATE"
                                                                            )
                                                                        }
                                                                        id={`invoiceCogginsCertificate${logAndLabel.log.id}`}
                                                                        icon={
                                                                            <FontAwesomeIcon icon={faFileInvoiceDollar} style={{ margin: "0 2px" }} />
                                                                        }
                                                                        title={
                                                                            (hfs.owners.find(o => o.cogginsInvoice !== null)
                                                                                ? "Update"
                                                                                : "Generate") +
                                                                            " Invoice for missing Registration Certificate/Coggins Certificate"
                                                                        }
                                                                        buttonStyle="btn btn-info btn-sm mr-2 mb-1"
                                                                        disabled={hfs.owners.length === 0}
                                                                    />
                                                                )}
                                                        </td>
                                                        {this.props.showStatus && i === 0 && (
                                                            <td rowSpan={logAndLabel.log.files.length} style={{ textAlign: "center" }}>
                                                                <ComplianceActions
                                                                    logAndLabel={logAndLabel}
                                                                    markLogNotCompliant={this.markLogNotCompliant}
                                                                    handleOverrideFile={this.handleOverrideFile}
                                                                />
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))
                                        )
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <FormDialog
                    title={`Override Not-compliant: ${this.state.currentOverrideType && this.state.currentOverrideType.name}`}
                    modalSize="modal-lg"
                    show={this.state.showNewOverrideDialog}
                    onClose={() => this.setState({ showNewOverrideDialog: false })}
                >
                    <form className={"px-2"} noValidate={true} onSubmit={e => this.submitOverride(e)} ref={ref => (this.formNotes = ref)}>
                        <div className="form-row">
                            <TextareaGroup
                                label="Notes"
                                rows={5}
                                name="currentOverrideNote"
                                className="col-md-12"
                                value={this.state.currentOverrideNote}
                                onChange={e => this.setState({ currentOverrideNote: e.target.value })}
                            />
                        </div>
                        <div className="form-row text-right">
                            <button
                                type="button"
                                className="btn btn-secondary mr-auto"
                                onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    this.clearOverrideForm();
                                }}
                            >
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary">
                                Override
                            </button>
                        </div>
                    </form>
                </FormDialog>
            </>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/horses-for-sale/DocumentsAndComplianceComponent.tsx